import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import './App.css';

const App = () => {
  const [files, setFiles] = useState([]);
  const [commonColumns, setCommonColumns] = useState([]);
  const [selectedMergeKey, setSelectedMergeKey] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [mergeType, setMergeType] = useState('advanced');
  const [feedback, setFeedback] = useState({ message: '', type: '' });

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length < 2) {
      setFeedback({ message: 'Please upload at least 2 Excel files.', type: 'error' });
      return;
    }

    setFiles(acceptedFiles);
    setIsProcessing(true);
    setFeedback({ message: '', type: '' });

    try {
      const allData = [];
      const allHeaders = [];

      for (const file of acceptedFiles) {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length > 0) {
          const headers = jsonData[0];
          const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== null && cell !== ''));
          allHeaders.push(headers);
          allData.push({ headers, rows, fileName: file.name });
        }
      }

      const common = allHeaders.reduce((acc, headers) => {
        if (acc.length === 0) return headers;
        return acc.filter(col => headers.includes(col));
      }, []);

      setCommonColumns(common);
      setParsedData(allData);
    } catch (error) {
      setFeedback({ message: 'Error reading files: ' + error.message, type: 'error' });
    }

    setIsProcessing(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: true
  });

  const performAdvancedMerge = () => {
    if (!selectedMergeKey) {
      setFeedback({ message: 'Please select a merge key for advanced merge.', type: 'error' });
      return;
    }

    setIsProcessing(true);
    setFeedback({ message: '', type: '' });

    try {
      const allKeys = new Set();
      const allColumns = new Set();
      const dataByKey = {};

      parsedData.forEach((fileData) => {
        const { headers, rows } = fileData;
        const keyColumnIndex = headers.indexOf(selectedMergeKey);

        if (keyColumnIndex === -1) return;

        headers.forEach(header => {
          if (header !== selectedMergeKey) {
            allColumns.add(header);
          }
        });

        rows.forEach(row => {
          const keyValue = row[keyColumnIndex];
          if (keyValue !== undefined && keyValue !== null && keyValue !== '') {
            allKeys.add(keyValue);
            
            if (!dataByKey[keyValue]) {
              dataByKey[keyValue] = {};
            }

            headers.forEach((header, colIndex) => {
              if (header !== selectedMergeKey) {
                const currentValue = row[colIndex];
                if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                  if (dataByKey[keyValue][header]) {
                    if (dataByKey[keyValue][header] === currentValue) {
                      dataByKey[keyValue][header] = currentValue;
                    } else {
                      dataByKey[keyValue][header] += ` | ${currentValue} (from ${fileData.fileName})`;
                    }
                  } else {
                    dataByKey[keyValue][header] = currentValue;
                  }
                }
              }
            });
          }
        });
      });

      const finalHeaders = [selectedMergeKey, ...Array.from(allColumns).sort()];
      const finalData = [finalHeaders];
      
      Array.from(allKeys).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).forEach(key => {
        const row = [key];
        Array.from(allColumns).sort().forEach(column => {
          const value = dataByKey[key] && dataByKey[key][column] ? dataByKey[key][column] : '';
          row.push(value);
        });
        finalData.push(row);
      });

      downloadExcel(finalData, 'merged_advanced_output.xlsx');
      setFeedback({ message: 'Files merged successfully!', type: 'success' });
    } catch (error) {
      setFeedback({ message: 'Error merging files: ' + error.message, type: 'error' });
    }

    setIsProcessing(false);
  };

  const performSimpleMerge = () => {
    setIsProcessing(true);
    setFeedback({ message: '', type: '' });

    try {
      const firstHeaders = JSON.stringify(parsedData[0].headers.slice().sort());
      for (let i = 1; i < parsedData.length; i++) {
        if (JSON.stringify(parsedData[i].headers.slice().sort()) !== firstHeaders) {
          setFeedback({ message: 'For simple merge, all files must have identical headers.', type: 'error' });
          setIsProcessing(false);
          return;
        }
      }

      const finalHeaders = [...parsedData[0].headers, 'SourceFile'];
      let allRows = [];
      parsedData.forEach(fileData => {
        const rowsWithSource = fileData.rows.map(row => [...row, fileData.fileName]);
        allRows = [...allRows, ...rowsWithSource];
      });

      const finalData = [finalHeaders, ...allRows];
      downloadExcel(finalData, 'merged_simple_output.xlsx');
      setFeedback({ message: 'Files merged successfully!', type: 'success' });
    } catch (error) {
      setFeedback({ message: 'Error during simple merge: ' + error.message, type: 'error' });
    }
    
    setIsProcessing(false);
  };

  const handleMergeClick = () => {
    if (mergeType === 'advanced') {
      performAdvancedMerge();
    } else {
      performSimpleMerge();
    }
  };
  
  const downloadExcel = (data, filename) => {
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Merged Data');
    XLSX.writeFile(workbook, filename);
  };

  const resetTool = () => {
    setFiles([]);
    setCommonColumns([]);
    setSelectedMergeKey('');
    setParsedData([]);
    setFeedback({ message: '', type: '' });
  };

  return (
    <div className="app-container">
      <div className="content-wrapper">
        
        {/* Main Tool Section */}
        <div className="main-section">
          {/* Header */}
          <div className="header-section">
            <h1 className="main-title">📊 Excel Merge Pro</h1>
            <p className="subtitle">Intelligent Data Consolidation Tool</p>
          </div>
          
          {/* Main Content */}
          <div className="main-content">
            {feedback.message && (
              <div className={`feedback ${feedback.type}`}>
                {feedback.message}
              </div>
            )}

            {/* Upload Section */}
            <div className="upload-section">
              <label className="section-label">
                Upload Excel Files (Minimum 2)
              </label>
              <div 
                {...getRootProps()} 
                className={`dropzone ${isDragActive ? 'active' : ''}`}
              >
                <input {...getInputProps()} />
                <div className="upload-icon">📂</div>
                <p className="upload-primary">
                  {isDragActive ? 'Drop your files here...' : 'Drag & drop Excel files here'}
                </p>
                <p className="upload-secondary">
                  or click to select (.xlsx, .xls)
                </p>
              </div>
              
              {files.length > 0 && (
                <div className="files-list">
                  <p className="files-count">
                    {files.length} files selected:
                  </p>
                  <ul className="files-items">
                    {files.map(file => (
                      <li key={file.name} className="file-item">
                        <span className="file-check">✓</span>
                        {file.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Merge Type Selection */}
            {files.length > 0 && (
              <div className="merge-type-section">
                <label className="section-label">
                  Merge Type
                </label>
                <select
                  value={mergeType}
                  onChange={(e) => setMergeType(e.target.value)}
                  className="merge-select"
                >
                  <option value="advanced">🧠 Advanced Merge (Key-based, Conflict Handling)</option>
                  <option value="simple">🔄 Simple Merge (Append Rows with Source)</option>
                </select>
              </div>
            )}

            {/* Key Column Selection */}
            {mergeType === 'advanced' && commonColumns.length > 0 && (
              <div className="key-selection-section">
                <label className="section-label">
                  Merge Key Column
                </label>
                <select
                  value={selectedMergeKey}
                  onChange={(e) => setSelectedMergeKey(e.target.value)}
                  className="key-select"
                >
                  <option value="">Select a column...</option>
                  {commonColumns.map((column, index) => (
                    <option key={index} value={column}>{column}</option>
                  ))}
                </select>
                <p className="common-columns">
                  Common columns: {commonColumns.join(', ')}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="actions">
              <button
                onClick={handleMergeClick}
                disabled={!files.length || isProcessing || (mergeType === 'advanced' && !selectedMergeKey)}
                className="merge-button"
              >
                {isProcessing ? (
                  <>
                    Processing
                    <div className="spinner"></div>
                  </>
                ) : (
                  '🚀 Merge & Download'
                )}
              </button>
              
              <button
                onClick={resetTool}
                className="reset-button"
              >
                🔄 Reset
              </button>
            </div>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="info-section">
          <div className="info-header">
            <h2 className="info-title">🔺 How to Use This Tool</h2>
            <p className="info-subtitle">Simple 4-Step Process</p>
          </div>
          
          <div className="info-content">
            {/* Triangle */}
            <div className="triangle-wrapper">
              <div className="triangle-container">
                <div className="triangle"></div>
                <div className="triangle-text">
                  UPLOAD<br/>↓<br/>SELECT<br/>↓<br/>MERGE
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="steps-grid">
              {[
                {
                  number: '1',
                  title: 'Upload Files',
                  desc: 'Drag & drop or click to select 2 or more Excel files (.xlsx, .xls). The tool will automatically analyze your files.'
                },
                {
                  number: '2',
                  title: 'Choose Merge Type',
                  desc: 'Select between Simple Merge (for identical structures) or Advanced Merge (for key-based merging with conflict handling).'
                },
                {
                  number: '3',
                  title: 'Select Key Column',
                  desc: 'For Advanced Merge, choose a common column that will serve as the merge key (like ID, Email, etc.).'
                },
                {
                  number: '4',
                  title: 'Download Result',
                  desc: 'Click "Merge & Download" to process your files and automatically download the merged Excel file.'
                }
              ].map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-number">
                    {step.number}
                  </div>
                  <h3 className="step-title">
                    {step.title}
                  </h3>
                  <p className="step-desc">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Simple Merge Example */}
        <div className="example-section">
          <div className="example-header simple">
            <h2 className="example-title">🔄 Simple Merge Example</h2>
            <p className="example-subtitle">Best for files with identical column structures</p>
          </div>
          
          <div className="example-content">
            <div className="merge-visual">
              <div className="merge-file">
                <h4 className="file-title">📄 file1.xlsx</h4>
                <table className="example-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Age</th>
                      <th>City</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>John</td>
                      <td>25</td>
                      <td>New York</td>
                    </tr>
                    <tr>
                      <td>Sarah</td>
                      <td>30</td>
                      <td>Boston</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="merge-symbol">+</div>

              <div className="merge-file">
                <h4 className="file-title">📄 file2.xlsx</h4>
                <table className="example-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Age</th>
                      <th>City</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Mike</td>
                      <td>28</td>
                      <td>Chicago</td>
                    </tr>
                    <tr>
                      <td>Lisa</td>
                      <td>35</td>
                      <td>Miami</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="merge-symbol">=</div>

              <div className="merge-output">
                <h4 className="file-title">📊 final_output.xlsx</h4>
                <table className="example-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Age</th>
                      <th>City</th>
                      <th className="source-col">SourceFile</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>John</td>
                      <td>25</td>
                      <td>New York</td>
                      <td className="source-cell">file1.xlsx</td>
                    </tr>
                    <tr>
                      <td>Sarah</td>
                      <td>30</td>
                      <td>Boston</td>
                      <td className="source-cell">file1.xlsx</td>
                    </tr>
                    <tr>
                      <td>Mike</td>
                      <td>28</td>
                      <td>Chicago</td>
                      <td className="source-cell">file2.xlsx</td>
                    </tr>
                    <tr>
                      <td>Lisa</td>
                      <td>35</td>
                      <td>Miami</td>
                      <td className="source-cell">file2.xlsx</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-box success">
              <div className="info-box-title">
                ✅ Simple Merge Result:
              </div>
              <p className="info-box-text">
                All rows from both files are combined with an additional <strong>SourceFile</strong> column to track data origin. Perfect for consolidating identical datasets!
              </p>
            </div>
          </div>
        </div>

        {/* Advanced Merge Example */}
        <div className="example-section">
          <div className="example-header advanced">
            <h2 className="example-title">🧠 Advanced Merge Example</h2>
            <p className="example-subtitle">Best for files with different structures sharing a common key</p>
          </div>
          
          <div className="example-content">
            <div className="merge-visual advanced">
              <div className="merge-file">
                <h4 className="file-title">📄 file1.xlsx</h4>
                <table className="example-table">
                  <thead>
                    <tr>
                      <th className="key-col">ID</th>
                      <th>Name</th>
                      <th>Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="key-cell">101</td>
                      <td>John Smith</td>
                      <td>IT</td>
                    </tr>
                    <tr>
                      <td className="key-cell">102</td>
                      <td>Sarah Johnson</td>
                      <td>HR</td>
                    </tr>
                    <tr>
                      <td className="key-cell">103</td>
                      <td>Mike Brown</td>
                      <td>Finance</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="merge-symbol advanced">🔄</div>

              <div className="merge-file">
                <h4 className="file-title">📄 file2.xlsx</h4>
                <table className="example-table">
                  <thead>
                    <tr>
                      <th className="key-col">ID</th>
                      <th>Salary</th>
                      <th>Name</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="key-cell">101</td>
                      <td>75000</td>
                      <td>John Smith</td>
                      <td>Active</td>
                    </tr>
                    <tr>
                      <td className="key-cell">102</td>
                      <td>65000</td>
                      <td>Sarah Williams</td>
                      <td>Active</td>
                    </tr>
                    <tr>
                      <td className="key-cell">104</td>
                      <td>80000</td>
                      <td>Lisa Davis</td>
                      <td>Active</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="merge-symbol">=</div>

              <div className="merge-output">
                <h4 className="file-title">📊 final_output.xlsx</h4>
                <table className="example-table advanced">
                  <thead>
                    <tr>
                      <th className="key-col">ID</th>
                      <th>Department</th>
                      <th>Name</th>
                      <th>Salary</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="key-cell">101</td>
                      <td>IT</td>
                      <td>John Smith</td>
                      <td>75000</td>
                      <td>Active</td>
                    </tr>
                    <tr>
                      <td className="key-cell">102</td>
                      <td>HR</td>
                      <td className="conflict">Sarah Johnson | Sarah Williams (from file2.xlsx)</td>
                      <td>65000</td>
                      <td>Active</td>
                    </tr>
                    <tr>
                      <td className="key-cell">103</td>
                      <td>Finance</td>
                      <td>Mike Brown</td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td className="key-cell">104</td>
                      <td></td>
                      <td>Lisa Davis</td>
                      <td>80000</td>
                      <td>Active</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-box info">
              <h4 className="info-box-title">
                🔍 Advanced Merge Intelligence:
              </h4>
              <ul className="info-list">
                <li><strong>ID 101:</strong> Perfect match - "John Smith" appears in both files, so merged cleanly</li>
                <li><strong>ID 102:</strong> Name conflict detected - Combined as "Sarah Johnson | Sarah Williams (from file2.xlsx)"</li>
                <li><strong>ID 103:</strong> Only in file1 - Salary and Status columns remain empty</li>
                <li><strong>ID 104:</strong> Only in file2 - Department column remains empty</li>
              </ul>
            </div>

            <div className="info-box warning">
              <div className="info-box-title">
                ⚠️ Conflict Handling:
              </div>
              <p className="info-box-text">
                When the same ID has different values for the same column, the tool combines them with source file information to preserve all data.
              </p>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="comparison-section">
          <div className="comparison-header">
            <h2 className="comparison-title">⚖️ Merge Type Comparison</h2>
            <p className="comparison-subtitle">Choose the right merge type for your needs</p>
          </div>
          
          <div className="comparison-content">
            <div className="table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="simple-col">Simple Merge</th>
                    <th className="advanced-col">Advanced Merge</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      feature: 'File Structure',
                      simple: 'Must have identical headers',
                      advanced: 'Can have different structures'
                    },
                    {
                      feature: 'Key Column Required',
                      simple: '❌ No',
                      advanced: '✅ Yes'
                    },
                    {
                      feature: 'Conflict Handling',
                      simple: 'None (appends all rows)',
                      advanced: 'Intelligent conflict resolution'
                    },
                    {
                      feature: 'Source Tracking',
                      simple: 'SourceFile column added',
                      advanced: 'Source info in conflicts only'
                    },
                    {
                      feature: 'Best Use Case',
                      simple: 'Combining identical datasets',
                      advanced: 'Merging different data sources'
                    },
                    {
                      feature: 'Empty Row Handling',
                      simple: 'Excluded automatically',
                      advanced: 'Handled based on key presence'
                    }
                  ].map((row, index) => (
                    <tr key={index} className="comparison-row">
                      <td className="feature-cell">{row.feature}</td>
                      <td className="simple-cell">{row.simple}</td>
                      <td className="advanced-cell">{row.advanced}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pro Tips */}
        <div className="tips-section">
          <div className="tips-header">
            <h2 className="tips-title">💡 Pro Tips</h2>
            <p className="tips-subtitle">Maximize your merging efficiency</p>
          </div>
          
          <div className="tips-content">
            <div className="tips-grid">
              {[
                {
                  icon: '💾',
                  title: 'File Preparation',
                  desc: 'Ensure your Excel files have clear headers in the first row. Remove any merged cells or formatting that might interfere with data reading.'
                },
                {
                  icon: '🔑',
                  title: 'Choose the Right Key',
                  desc: 'For Advanced Merge, select a unique identifier column like ID, Email, or Employee Number that exists in all files.'
                },
                {
                  icon: '🧹',
                  title: 'Data Cleanup',
                  desc: 'The tool automatically excludes completely empty rows, but ensure your key columns have consistent formatting (no extra spaces).'
                },
                {
                  icon: '📊',
                  title: 'Review Results',
                  desc: 'Always review the downloaded file, especially for Advanced Merge results with conflicts marked by source file information.'
                }
              ].map((tip, index) => (
                <div key={index} className="tip-card">
                  <div className="tip-icon">
                    {tip.icon}
                  </div>
                  <h3 className="tip-title">
                    {tip.title}
                  </h3>
                  <p className="tip-desc">
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
 <div className="footer">
        <p className="footer-title">
          🚀 Excel Merge Tool 
        </p>
        <p className="footer-text">
          Streamline your data consolidation process with intelligent merging!
        </p>
        <p className="footer-text">
          Designed & Developed by <span className="dev-name">Parimal Hodar</span> |{" "}
          <a href="mailto:parimalhodar.dev@gmail.com" className="footer-link">
            parimalhodar.dev@gmail.com
          </a>
        </p>
</div>
</div>
</div>
);
};

export default App;
